import { getRequiredMessage, InputType } from '@app/shared/models/input-type';
import { SelectableValue } from '@app/shared/models/process/selectable-value.model';
import {
    getMaskByUnit, getPlaceholderByUnit, getSuffixByUnit, Unit,
} from '@app/shared/models/unit';
import { IsNullable } from '@webmozarts/http-client';
import { Expose, Transform, Type } from 'class-transformer';
import { IsArray, IsBoolean, IsDefined, IsEnum, IsString, ValidateNested } from 'class-validator';

export class BaseValueDefinition {
    @IsDefined()
    id!: string;

    @IsDefined()
    globalId!: string;

    @IsDefined()
    label!: string;

    @IsDefined()
    @IsEnum(InputType)
    @Type()
    inputType!: InputType;

    @Type()
    @IsDefined()
    @IsNullable()
    @IsEnum(Unit)
    unit!: Unit | null;

    @IsDefined({ each: true })
    @IsArray()
    @ValidateNested()
    @Type(() => SelectableValue)
    selectableValues!: SelectableValue[];

    @IsDefined()
    @IsNullable()
    helpText!: string | null;

    @IsDefined()
    @IsBoolean()
    reviewInAdvanceNotification!: boolean;

    @IsDefined()
    @IsNullable()
    @IsString()
    dependsOnId!: string | null;

    @Expose()
    @IsDefined()
    @IsString()
    @Transform(({ obj }) => obj.inputType ? getRequiredMessage(obj.inputType as InputType) : '')
    requiredMessage!: string;

    @Expose()
    @IsDefined()
    @IsString()
    @Transform(({ obj }) => getPlaceholderByUnit(obj.unit as Unit | null))
    placeholder!: string;

    @Expose()
    @IsDefined()
    @IsString()
    @Transform(({ obj }) => getSuffixByUnit(obj.unit as Unit | null))
    suffix!: string;

    @Expose()
    @IsDefined()
    @IsString()
    @Transform(({ obj }) => getMaskByUnit(obj.unit as Unit | null))
    mask!: string;
}

export function isDropdownWithTheFollowingValues(
    baseValueDefinition: BaseValueDefinition,
    expectedValues: string[],
): boolean {
    const { inputType, selectableValues } = baseValueDefinition;

    return inputType === InputType.DROPDOWN
        && selectableValuesEqualCanonicalizing(selectableValues, expectedValues);
}

function selectableValuesEqualCanonicalizing(
    selectableValues: SelectableValue[],
    expectedValues: string[],
): boolean {
    if (expectedValues.length !== selectableValues.length) {
        return false;
    }

    const expectedValuesSet = new Set(expectedValues);

    for (const selectableValue of selectableValues) {
        if (!expectedValuesSet.has(selectableValue.value)) {
            return false;
        }

        expectedValuesSet.delete(selectableValue.value);
    }

    return true;
}
